import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { ScoreCards } from "@/components/ScoreCards";
import { ChevronLeft, Activity, ShieldCheck, Scale, Loader2, Check, Plus, X, Sparkles } from "lucide-react";

const primaryBtn = "px-6 py-3 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all shadow-md active:scale-[0.99] disabled:opacity-60";

export default function ScoreCase() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [injuries, setInjuries] = useState([]); // [{type, locations:[]}]
  const [coverage, setCoverage] = useState(null);
  const [liability, setLiability] = useState(null);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, cs] = await Promise.all([api.get("/scoring/config"), api.get(`/cases/${caseId}`)]);
        setConfig(c.data);
        setCaseData(cs.data);
        if (cs.data.scores) {
          setResult(cs.data.scores);
          const inj = cs.data.scores.injury?.inputs?.injuries || [];
          setInjuries(inj);
          setCoverage(cs.data.scores.coverage?.inputs?.option || null);
          setLiability(cs.data.scores.liability?.inputs?.option || null);
        }
      } catch { toast.error("Could not load"); navigate("/dashboard"); }
    })();
  }, [caseId, navigate]);

  const toggleInjury = (key) => {
    setInjuries((prev) => {
      const has = prev.some((i) => i.type === key);
      if (has) return prev.filter((i) => i.type !== key);
      // "No injury" is mutually exclusive with all other injuries.
      if (key === "none") return [{ type: "none", locations: [] }];
      return [...prev.filter((i) => i.type !== "none"), { type: key, locations: [] }];
    });
  };
  const toggleLocation = (key, loc) => {
    setInjuries((prev) => prev.map((i) => i.type === key
      ? { ...i, locations: i.locations.includes(loc) ? i.locations.filter((l) => l !== loc) : [...i.locations, loc] }
      : i));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/score`, {
        injuries, coverage_option: coverage, liability_option: liability,
      });
      setResult(data);
      toast.success("Scores calculated");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Could not calculate scores"); }
    finally { setSaving(false); }
  };

  if (!config) return <AppLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div></AppLayout>;

  const injCfg = config.categories.injury;
  const covCfg = config.categories.coverage;
  const liabCfg = config.categories.liability;
  const hasNone = injuries.some((i) => i.type === "none");

  return (
    <AppLayout>
      <Link to={`/cases/${caseId}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-navy mb-5"><ChevronLeft className="w-4 h-4" /> Back to case</Link>
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-navy flex items-center justify-center"><Sparkles className="w-6 h-6 text-white" /></div>
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Case Scoring</p>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-navy">Crash Ready Score</h1>
        </div>
      </div>

      {result && (
        <div className="mt-6" data-testid="score-results">
          <ScoreCards scores={result} />
          <p className="mt-3 text-xs text-slate-400">These scores measure different aspects of your case — they are informational and not legal advice.</p>
          <ImproveMyCase caseId={caseId} navigate={navigate} />
          <ConsentToggle caseId={caseId} caseData={caseData} />
        </div>
      )}

      {/* Injuries */}
      <section className="mt-8 bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-2"><Activity className="w-5 h-5 text-cobalt" /><h2 className="font-heading font-bold text-navy text-lg">{injCfg.prompt}</h2></div>
        <p className="text-sm text-slate-500 mt-1">Select every injury that applies, then choose where each one is located.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {injCfg.options.map((o) => {
            const on = injuries.some((i) => i.type === o.key);
            return (
              <button key={o.key} onClick={() => toggleInjury(o.key)} data-testid={`injury-${o.key}`}
                className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-all ${on ? "bg-cobalt text-white border-cobalt" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {on && <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}{o.label}
              </button>
            );
          })}
        </div>

        {injuries.filter((i) => i.type !== "none").length > 0 && (
          <div className="mt-6 space-y-4">
            {injuries.filter((i) => i.type !== "none").map((i) => {
              const label = injCfg.options.find((o) => o.key === i.type)?.label;
              return (
                <div key={i.type} className="rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid={`injury-locations-${i.type}`}>
                  <div className="text-sm font-semibold text-navy mb-2">Where is the {label} located?</div>
                  <div className="flex flex-wrap gap-1.5">
                    {injCfg.body_locations.map((loc) => {
                      const on = i.locations.includes(loc);
                      return (
                        <button key={loc} onClick={() => toggleLocation(i.type, loc)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${on ? "bg-navy text-white border-navy" : "bg-white border-slate-200 text-slate-500 hover:bg-white"}`}>
                          {loc}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {hasNone && <p className="mt-3 text-xs text-amber-600">"No injury" is selected — injury severity will grade as F.</p>}
      </section>

      {/* Coverage */}
      <OptionSection title={covCfg.prompt} icon={ShieldCheck} options={covCfg.options} value={coverage} onChange={setCoverage} testPrefix="coverage" />
      {/* Liability */}
      <OptionSection title={liabCfg.prompt} icon={Scale} options={liabCfg.options} value={liability} onChange={setLiability} testPrefix="liability" />

      <div className="mt-8 flex justify-end">
        <button onClick={submit} disabled={saving} data-testid="calculate-score-btn" className={primaryBtn}>
          {saving ? "Calculating…" : result ? "Recalculate Scores" : "Calculate Scores"}
        </button>
      </div>
    </AppLayout>
  );
}

function OptionSection({ title, icon: Icon, options, value, onChange, testPrefix }) {
  return (
    <section className="mt-6 bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="flex items-center gap-2"><Icon className="w-5 h-5 text-cobalt" /><h2 className="font-heading font-bold text-navy text-lg">{title}</h2></div>
      <div className="mt-4 space-y-2">
        {options.map((o) => {
          const on = value === o.key;
          return (
            <button key={o.key} onClick={() => onChange(o.key)} data-testid={`${testPrefix}-${o.key}`}
              className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all ${on ? "border-cobalt bg-cobalt-light" : "border-slate-200 hover:bg-slate-50"}`}>
              <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${on ? "border-cobalt bg-cobalt" : "border-slate-300"}`}>{on && <Check className="w-3 h-3 text-white" />}</span>
              <span className={`text-sm font-medium ${on ? "text-navy" : "text-slate-600"}`}>{o.label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ImproveMyCase({ caseId, navigate }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/cases/${caseId}/missing`).then(({ data }) => setItems(data.items)).catch(() => {}); }, [caseId]);
  const missing = items.filter((i) => !i.present);
  if (!items.length) return null;
  return (
    <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="improve-my-case">
      <h3 className="font-heading font-bold text-navy text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-cobalt" /> Improve My Case</h3>
      <p className="text-sm text-slate-500 mt-1">{missing.length === 0 ? "Great — your case file looks complete!" : "Add these to strengthen your case. Scores recalculate as you add information."}</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {items.map((i) => (
          <div key={i.key} className={`flex items-center justify-between px-4 py-3 rounded-2xl border ${i.present ? "border-green-100 bg-green-50/50" : "border-slate-200"}`}>
            <span className={`flex items-center gap-2 text-sm font-medium ${i.present ? "text-green-600" : "text-slate-600"}`}>
              {i.present ? <Check className="w-4 h-4" /> : <X className="w-4 h-4 text-slate-300" />} {i.label}
            </span>
            {!i.present && <button onClick={() => navigate(`/new-accident?case=${caseId}`)} className="text-xs font-semibold text-cobalt">Add</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ConsentToggle({ caseId, caseData }) {
  const [on, setOn] = useState(!!caseData?.consent_marketplace);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/cases/${caseId}/consent`, { consent: !on }); setOn(data.consent_marketplace); toast.success(data.consent_marketplace ? "Your case is now available to attorneys" : "Removed from attorney marketplace"); }
    catch { toast.error("Could not update"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 bg-navy text-white rounded-3xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="consent-toggle">
      <div>
        <h3 className="font-heading font-bold text-lg">Attorney Matching</h3>
        <p className="text-sm text-slate-300 mt-1 max-w-md">Allow verified attorneys to view your case (shown only as first name + last initial — no contact details) until one claims it.</p>
      </div>
      <button onClick={toggle} disabled={busy} data-testid="consent-btn" className={`shrink-0 px-6 py-3 rounded-full font-medium transition-all active:scale-95 ${on ? "bg-green-500 text-white" : "bg-white text-navy"}`}>
        {on ? "✓ Available to Attorneys" : "Make My Case Available"}
      </button>
    </div>
  );
}

